<template>
    <div v-if="isCurrentFilterStep('filterRule')" class="flex flex-col gap-y-3">
        <span class="text-gray-900 text-lg font-medium">URL</span>
        <FilterRule
            :filter="selectedFilter"
            :isUpdated="isEditFilter"
            :showHeader="false"
            :isWorkflowTrigger="true"
            @applyFilter="applyFilter"
        />
    </div>
    <FilterList
        v-if="isCurrentFilterStep('filterList')"
        :savedFiltersGroups="workflowStore.pageVisitedFiltersGroups"
        :isWorkflowTrigger="true"
        @addNewFilter="addNewFilterToGroup"
        @editFilter="editFilter"
        @clearFilters="clearFilters"
        @removeGroup="removeGroup"
        @removeFilter="removeFilter"
        @addNewGroup="addNewGroup"
    />
</template>

<script setup lang="ts">
import { ref, watch } from "vue"
import { Filter, FiltersGroup } from "@/shared/models/filter"
import FilterRule from "@/components/filters/FilterRule.vue"
import FilterList from "@/components/filters/FilterList.vue"
import { useWorkflowStore } from "@/stores/workflow"

const workflowStore = useWorkflowStore()

const selectedFilterIndex = ref(0)
const selectedFilterGroup = ref(0)
const isEditFilter = ref(false)

const selectedFilter = ref<Filter>({
    field: "properties.url",
    type: "string",
    rule: "",
    value: "",
    displayName: "Url",
})

function isFilterEmpty(filter: Filter): boolean {
    return (
        (filter.value === "" ||
            (Array.isArray(filter.value) && filter.value.length === 0) ||
            (Array.isArray(filter.value) && filter.value.every((val) => val === ""))) &&
        filter.rule !== "isEmpty" &&
        filter.rule !== "isNotEmpty" &&
        filter.rule !== "isAny"
    )
}

function openFiltersList() {
    workflowStore.currentStep = "filterList"
    isEditFilter.value = false
}

function isCurrentFilterStep(step: string): boolean {
    return workflowStore.currentStep === step
}

async function applyFilter(filter: Filter) {
    selectedFilter.value = filter
    isEditFilter.value ? editFilterGroup(filter, selectedFilterIndex.value) : addFilterToGroup(filter)
    openFiltersList()

    if (isFilterEmpty(filter)) {
        // Filter out non-empty filters
        workflowStore.pageVisitedFiltersGroups?.forEach((filterGroup: FiltersGroup) => {
            filterGroup.filters = filterGroup.filters.filter((f: Filter) => !isFilterEmpty(f))
        })

        // Filter out filter groups with no filters
        workflowStore.pageVisitedFiltersGroups = workflowStore.pageVisitedFiltersGroups?.filter(
            (filterGroup: FiltersGroup) => filterGroup.filters.length > 0
        )

        if (workflowStore.pageVisitedFiltersGroups?.length === 0) {
            workflowStore.pageVisitedFiltersGroups = [{ filters: [] }] // Ensure at least one empty group
            openFiltersList()
        }
    }
    resetSelectedFilter()
}

function editFilterGroup(filter: Filter, filterIndex: number) {
    workflowStore.pageVisitedFiltersGroups[selectedFilterGroup.value].filters[filterIndex] = filter
}

function addFilterToGroup(filter: Filter) {
    if (!workflowStore.pageVisitedFiltersGroups[selectedFilterGroup.value]) {
        workflowStore.pageVisitedFiltersGroups?.push({ filters: [filter] })
    } else {
        workflowStore.pageVisitedFiltersGroups[selectedFilterGroup.value].filters.push(filter)
    }
}

function addNewFilterToGroup(groupIndex: number) {
    isEditFilter.value = false
    selectedFilterGroup.value = groupIndex
    resetSelectedFilter()
    workflowStore.currentStep = "filterRule"
}

function editFilter(filter: Filter, groupIndex: number, filterIndex: number) {
    selectedFilter.value = {
        field: filter.field,
        type: filter.type,
        rule: filter.rule,
        value: filter.value,
        displayName: filter.displayName,
    }
    selectedFilterIndex.value = filterIndex
    selectedFilterGroup.value = groupIndex
    isEditFilter.value = true
    workflowStore.currentStep = "filterRule"
}

function clearFilters() {
    workflowStore.pageVisitedFiltersGroups = [{ filters: [] }]
    selectedFilterGroup.value = 0
    resetSelectedFilter()
    workflowStore.currentStep = "filterRule"
    isEditFilter.value = false
}

function resetSelectedFilter() {
    selectedFilter.value = {
        field: "properties.url",
        type: "string",
        rule: "",
        value: "",
        displayName: "Url",
    }
}

function removeGroup(groupIndex: number) {
    workflowStore.pageVisitedFiltersGroups?.splice(groupIndex, 1)
    if (workflowStore.pageVisitedFiltersGroups?.length === 0) {
        workflowStore.pageVisitedFiltersGroups = [{ filters: [] }]
    }
    selectedFilterGroup.value = Math.min(selectedFilterGroup.value, workflowStore.pageVisitedFiltersGroups.length - 1)
}

function removeFilter(groupIndex: number, filterIndex: number) {
    workflowStore.pageVisitedFiltersGroups[groupIndex].filters.splice(filterIndex, 1)
    if (workflowStore.pageVisitedFiltersGroups[groupIndex].filters.length === 0) {
        workflowStore.pageVisitedFiltersGroups?.splice(groupIndex, 1)
    }
    if (workflowStore.pageVisitedFiltersGroups?.length === 0) {
        workflowStore.pageVisitedFiltersGroups = [{ filters: [] }]
    }
    selectedFilterGroup.value = Math.min(selectedFilterGroup.value, workflowStore.pageVisitedFiltersGroups.length - 1)
}

function addNewGroup() {
    workflowStore.pageVisitedFiltersGroups?.push({ filters: [] })
    selectedFilterGroup.value = workflowStore.pageVisitedFiltersGroups.length - 1
    resetSelectedFilter()
    isEditFilter.value = false
    workflowStore.currentStep = "filterRule"
}

watch(
    () => workflowStore.pageVisitedFiltersGroups,
    () => (workflowStore.areButtonDisabled = workflowStore.pageVisitedFiltersGroups[0]?.filters.length === 0),
    { deep: true }
)
</script>
